/* eslint-disable max-lines */
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';

import { QUERY_CLIENT } from '@services/query.service';

import './web-layout.css';

const COMPLETED_QUESTIONS_KEY = 'webCompletedQuestions';
const INTRODUCTION_COMPLETE_KEY = 'webIntroductionComplete';

function readCompletedQuestions() {
  try {
    const storedQuestions = JSON.parse(sessionStorage.getItem(COMPLETED_QUESTIONS_KEY));

    return Array.isArray(storedQuestions) ? storedQuestions : [];
  } catch {
    return [];
  }
}

function getQuestionNumber(pathname) {
  const match = pathname.match(/^\/question([1-5])\/?$/);

  return match ? Number(match[1]) : null;
}

function WebLayout() {
  const mapContainerRef = useRef(null);
  const participantStartedRef = useRef(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const normalizedPathname = pathname.replace(/\/+$/, '') || '/';
  const isIntroductionRoute = normalizedPathname === '/';
  const questionNumber = getQuestionNumber(normalizedPathname);
  const [lastAnswer, setLastAnswer] = useState(null);
  const [participantId, setParticipantId] = useState(
    () => (isIntroductionRoute ? null : sessionStorage.getItem('webParticipantId')),
  );
  const [completedQuestions, setCompletedQuestions] = useState(
    () => (isIntroductionRoute ? [] : readCompletedQuestions()),
  );
  const hasIntroduction = Boolean(
    sessionStorage.getItem('webParticipantId')
      && sessionStorage.getItem(INTRODUCTION_COMPLETE_KEY) === 'true',
  );
  const previousQuestion = questionNumber > 1 ? `question${questionNumber - 1}` : null;
  const hasPreviousAnswer = !previousQuestion || completedQuestions.includes(previousQuestion);
  const shouldRedirect = !isIntroductionRoute && (!hasIntroduction || !hasPreviousAnswer);

  const createAnswerMutation = useMutation({
    mutationFn: async ({ activeParticipantId, questionId, answer }) => {
      const { data } = await axios.post('/api/participants/answer', {
        participantId: activeParticipantId,
        questionId,
        answer,
        source: 'web',
      });

      return data;
    },
    onSuccess: () => {
      QUERY_CLIENT.invalidateQueries({ queryKey: ['answers'] });
      QUERY_CLIENT.invalidateQueries({ queryKey: ['participant-answers'] });
    },
  });

  const createIntroductionMutation = useMutation({
    mutationFn: ({ activeParticipantId, introduction }) => axios.post('/api/participants/introduction', {
      participantId: activeParticipantId,
      introduction,
      source: 'web',
    }),
  });

  useEffect(() => {
    if (!isIntroductionRoute) {
      participantStartedRef.current = false;
      return;
    }
    if (participantStartedRef.current) return;

    sessionStorage.removeItem('webParticipantId');
    sessionStorage.removeItem(COMPLETED_QUESTIONS_KEY);
    sessionStorage.removeItem(INTRODUCTION_COMPLETE_KEY);
    setParticipantId(null);
    setCompletedQuestions([]);
    setLastAnswer(null);
  }, [isIntroductionRoute]);

  useEffect(() => {
    if (!isIntroductionRoute && shouldRedirect) navigate('/', { replace: true });
  }, [isIntroductionRoute, navigate, shouldRedirect]);

  const isQuestionRoute = questionNumber !== null;

  if (shouldRedirect) return null;

  return (
    <div className={`web-layout${isQuestionRoute ? ' web-layout--question' : ' web-layout--introduction'}`}>
      <div ref={mapContainerRef} className="web-layout__map-container" />
      <div className="web-layout__content">
        <Outlet
          context={{
            mapContainerRef,
            onStartParticipant: (introduction) => {
              const nextParticipantId = uuidv4();

              participantStartedRef.current = true;
              sessionStorage.setItem('webParticipantId', nextParticipantId);
              sessionStorage.setItem(INTRODUCTION_COMPLETE_KEY, 'true');
              sessionStorage.removeItem(COMPLETED_QUESTIONS_KEY);
              setParticipantId(nextParticipantId);
              setCompletedQuestions([]);
              createIntroductionMutation.mutate({
                activeParticipantId: nextParticipantId,
                introduction,
              });
            },
            onSelectAnswer: (questionId, answer) => {
              const activeParticipantId = participantId || sessionStorage.getItem('webParticipantId');

              if (!activeParticipantId) {
                navigate('/', { replace: true });
                return;
              }

              setLastAnswer({ participantId, questionId, answer });
              setCompletedQuestions((currentQuestions) => {
                const nextQuestions = currentQuestions.includes(questionId)
                  ? currentQuestions
                  : [...currentQuestions, questionId];

                sessionStorage.setItem(COMPLETED_QUESTIONS_KEY, JSON.stringify(nextQuestions));
                return nextQuestions;
              });
              createAnswerMutation.mutate({
                activeParticipantId,
                questionId,
                answer,
              });
            },
            lastAnswer,
          }}
        />
      </div>
    </div>
  );
}

export default WebLayout;